import { InfoIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { useModal, type ModalProps } from '@components/modal';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import {
  useGetSkillsQuery,
  useGetWorkspaceSkillOverridesQuery,
  useToggleWorkspaceSkillOverrideMutation,
  type Skill,
} from '@generated/graphql';
import { Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import posthog from 'posthog-js';
import { useState } from 'react';
import { ResourceTag } from '../ResourceTag';
import { ResourceListItem, ResourceTransferListButtons } from '../ResourceTransferList';

type ManageImportedSkillsModalProps = {
  organizationId: string;
  workspaceId: string;
  onChange?: VoidFunction;
};

const ManageImportedSkillsModal = ({
  organizationId,
  workspaceId,
  onChange,
}: ManageImportedSkillsModalProps) => {
  const { palette, breakpoints } = useTheme();
  const isVertical = useMediaQuery(breakpoints.down('md'));

  const [selectedCurrent, setSelectedCurrent] = useState<Set<string>>(new Set());
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [transferring, setTransferring] = useState(false);

  const [toggleOverride] = useToggleWorkspaceSkillOverrideMutation();

  const { data: orgData, refetch: refetchOrgData } = useGetSkillsQuery({
    variables: { mode: 'organization', organizationId },
  });

  const { data: userData, refetch: refetchUserData } = useGetSkillsQuery({
    variables: { mode: 'user' },
  });

  const { data: overridesData, refetch: refetchOverridesData } = useGetWorkspaceSkillOverridesQuery(
    {
      variables: { workspaceId },
    },
  );

  const overriddenSkillIds = new Set(
    (overridesData?.getWorkspaceSkillOverrides || []).map((o) => o?.skillId),
  );

  const orgSkills = orgData?.getSkills || [];
  const userSkills = userData?.getSkills || [];

  const autoInheritedOrgSkills = orgSkills.filter((s) => s.enabled);
  const autoInheritedUserSkills = userSkills.filter((s) => s.enabled);

  // Imported = not auto-inherited AND has override (currently included)
  const currentSkills = [
    ...orgSkills
      .filter((s) => !s.enabled && overriddenSkillIds.has(s.skillId))
      .map((s) => ({ ...s, skillSource: 'organization' as const })),
    ...userSkills
      .filter((s) => !s.enabled && overriddenSkillIds.has(s.skillId))
      .map((s) => ({ ...s, skillSource: 'user' as const })),
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Available = not auto-inherited AND no override (not yet imported)
  const availableSkills = [
    ...orgSkills
      .filter((s) => !s.enabled && !overriddenSkillIds.has(s.skillId))
      .map((s) => ({ ...s, skillSource: 'organization' as const })),
    ...userSkills
      .filter((s) => !s.enabled && !overriddenSkillIds.has(s.skillId))
      .map((s) => ({ ...s, skillSource: 'user' as const })),
  ].sort((a, b) => a.name.localeCompare(b.name));

  const toggleCurrent = (id: string) => {
    setSelectedCurrent((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAvailable = (id: string) => {
    setSelectedAvailable((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddServers = async () => {
    if (selectedAvailable.size === 0) return;
    setTransferring(true);
    try {
      await Promise.all(
        Array.from(selectedAvailable).map((skillId) =>
          toggleOverride({
            variables: { workspaceId, skillId },
            onCompleted: () => {
              posthog.capture('skills:user_add-workspace_skills_transfer', {
                workspaceId,
                skillId,
              });
              void refetchOverridesData();
              void refetchOrgData();
              void refetchUserData();
              onChange?.();
            },
          }),
        ),
      );
      setSelectedAvailable(new Set());
    } finally {
      setTransferring(false);
    }
  };

  const handleRemoveServers = async () => {
    if (selectedCurrent.size === 0) return;
    setTransferring(true);
    try {
      await Promise.all(
        Array.from(selectedCurrent).map((skillId) =>
          toggleOverride({
            variables: { workspaceId, skillId },
            onCompleted: () => {
              posthog.capture('skills:user_remove-workspace_skills_transfer', {
                workspaceId,
                skillId,
              });
              void refetchOverridesData();
              void refetchOrgData();
              void refetchUserData();
              onChange?.();
            },
          }),
        ),
      );
      setSelectedCurrent(new Set());
    } finally {
      setTransferring(false);
    }
  };

  const hasAutoInherited = autoInheritedOrgSkills.length > 0 || autoInheritedUserSkills.length > 0;

  return (
    <Stack gap={3} sx={{ height: '100%' }}>
      {/* Auto-inherited MCP servers section (read-only) */}
      {hasAutoInherited && (
        <Stack gap={1} sx={{ flexShrink: 0 }}>
          <Typography
            className="deckard-text-input-label"
            variant="caption2"
            color={palette.input.label.default}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Auto-inherited agent skills
            <Tooltip title="Auto-inherited agent skills are enabled by default for all workspaces. You can override this by clicking the toggle button in the Agent Skills panel.">
              <InfoIcon size={16} />
            </Tooltip>
          </Typography>
          <Group flexWrap="wrap" gap={1}>
            {autoInheritedOrgSkills.map((skill) => (
              <ResourceTag key={skill.skillId} name={skill.name} source="organization" />
            ))}
            {autoInheritedUserSkills.map((skill) => (
              <ResourceTag key={skill.skillId} name={skill.name} source="user" />
            ))}
          </Group>
        </Stack>
      )}

      {/* Transfer list */}
      <Stack
        direction={isVertical ? 'column' : 'row'}
        gap={2}
        sx={{ flex: 1, minHeight: '400px', maxHeight: isVertical ? '70vh' : '600px' }}
      >
        {/* Current Imported MCP Servers */}
        <Stack flex={1} sx={{ overflow: 'hidden' }} gap={2}>
          <Typography variant="body2" color="foreground.paper.light">
            Imported agent skills ({currentSkills.length})
          </Typography>
          <Stack
            sx={{
              flex: 1,
              overflow: 'auto',
              py: 1,
              backgroundColor: palette.background.paperCard,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {currentSkills.length === 0 ? (
              <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
                <Typography variant="body2" color="foreground.paperCard.light">
                  No imported agent skills
                </Typography>
              </Stack>
            ) : (
              currentSkills.map((skill) => (
                <ResourceListItem<Skill>
                  key={skill.skillId}
                  resource={skill}
                  idKey="skillId"
                  tag={skill.scopeType === 'organization' ? 'Organization' : 'User'}
                  selected={selectedCurrent.has(skill.skillId)}
                  onToggle={toggleCurrent}
                />
              ))
            )}
          </Stack>
        </Stack>

        {/* Transfer Buttons */}
        <ResourceTransferListButtons
          onAdd={handleAddServers}
          onRemove={handleRemoveServers}
          isVertical={isVertical}
          addDisabled={selectedAvailable.size === 0 || transferring}
          removeDisabled={selectedCurrent.size === 0 || transferring}
        />

        {/* Available MCP Servers */}
        <Stack flex={1} sx={{ overflow: 'hidden' }} gap={2}>
          <Typography variant="body2" color="foreground.paper.light">
            Available agent skills
          </Typography>
          <Stack
            sx={{
              flex: 1,
              overflow: 'auto',
              py: 1,
              backgroundColor: palette.background.paperCard,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {availableSkills.length === 0 ? (
              <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
                <Typography variant="body2" color="foreground.paperCard.light">
                  No available agent skills found
                </Typography>
              </Stack>
            ) : (
              availableSkills.map((skill) => (
                <ResourceListItem<Skill>
                  key={skill.skillId}
                  resource={skill}
                  idKey="skillId"
                  tag={skill.scopeType === 'organization' ? 'Organization' : 'User'}
                  selected={selectedAvailable.has(skill.skillId)}
                  onToggle={toggleAvailable}
                />
              ))
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const useManageImportedSkillsModal = ({
  componentProps,
  modalProps,
}: {
  componentProps: Omit<ManageImportedSkillsModalProps, 'onClose'> & { onClose?: () => void };
  modalProps?: Partial<ModalProps>;
}) => {
  const { showModal } = useModal({
    component: ManageImportedSkillsModal,
    componentProps,
    modalProps: {
      title: 'Manage imported agent skills',
      sx: {
        width: '90vw',
        maxWidth: '1000px',
        display: 'flex',
        flexDirection: 'column',
      },
      ...modalProps,
    },
  });

  const showManageImportedSkillsModal = (
    dynamicComponentProps?: Partial<ManageImportedSkillsModalProps>,
  ) => {
    showModal({ componentProps: dynamicComponentProps });
  };

  return { showManageImportedSkillsModal };
};
